import { commonDateParamMappings } from '../../../api/common/mappings';
import { standardQuerySerializer } from '../../../api/serializers';
import { applyQueryConfig, ServiceHandler } from '../../../utils';

const getTransactionsQueryConfig = [
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      const newOrder = order || 'asc';
      const newOrderBy = orderBy === 'modified' ? 'paymentDate' : orderBy;

      switch (newOrderBy) {
        case 'paymentDate':
        case 'modified':
        case 'amountPaid':
          return `${newOrderBy},${newOrder}`;
        default:
          return newOrderBy ? `${newOrderBy}.keyword,${newOrder}` : null;
      }
    },
  },
  ...commonDateParamMappings,
];

const getTransactionsHandler = ServiceHandler({
  url: 'api/billing/v1/search',
  paramsSerializer: (params) =>
    standardQuerySerializer(applyQueryConfig(getTransactionsQueryConfig, params)),
});
export const getTransactions = (params: Record<string, unknown> = {}) =>
  getTransactionsHandler({ params });

const getPaymentMethodsHandler = ServiceHandler({
  url: `api/billing/v1/payment`,
});
export const getPaymentMethods = (params: Record<string, unknown> = {}) =>
  getPaymentMethodsHandler({ params });

const getACHDetailsHandler = ServiceHandler({
  url: `api/billing/v1/ach/:policyNumber`,
});
export const getACHDetails = ({
  policyNumber,
}: {
  policyNumber: string | number;
}) => getACHDetailsHandler(policyNumber);

const getPaymentProcessorHandler = ServiceHandler({
  url: `api/billing/v1/payment-processor`,
});
export const getPaymentProcessor = (params: Record<string, unknown> = {}) =>
  getPaymentProcessorHandler({ params });

const getOneIncKeyHandler = ServiceHandler({
  url: `api/billing/v1/oneInc/key`,
});
export const getOneIncKey = (params: Record<string, unknown> = {}) =>
  getOneIncKeyHandler({ params });

const getOneIncKeyByPolicyNumberHandler = ServiceHandler({
  url: `api/billing/v1/oneInc/:policyNumber`,
});
export const getOneIncKeyByPolicyNumber = ({
  policyNumber,
}: {
  policyNumber: string | number;
}) => getOneIncKeyByPolicyNumberHandler(policyNumber);

const paymentCompleteHandler = ServiceHandler({
  url: 'api/billing/v1/paid',
  method: 'post',
});
export const paymentComplete = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => paymentCompleteHandler({ data });

const adminPaymentCompleteHandler = ServiceHandler({
  url: 'api/billing/v1/payfor/:accountId',
  method: 'post',
});
export const adminPaymentComplete = (
  { accountId }: { accountId: string | number },
  data?: unknown
) => adminPaymentCompleteHandler(accountId, { data });

const savePaymentHandler = ServiceHandler({
  url: 'api/billing/v1/save',
  method: 'post',
});
export const savePayment = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => savePaymentHandler({ data });

const sendAccountingHandler = ServiceHandler({
  url: 'api/billing/v1/accounting',
  method: 'post',
});
export const sendAccounting = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => sendAccountingHandler({ data });

const confirmFinancingHandler = ServiceHandler({
  url: 'api/billing/v1/agreement',
  method: 'post',
});
export const confirmFinancing = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => confirmFinancingHandler({ data });

const installmentPlanFormHandler = ServiceHandler({
  url: 'api/billing/v1/finance',
  method: 'post',
});
export const installmentPlanForm = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => installmentPlanFormHandler({ data });

const getXpressDocumentHandler = ServiceHandler({
  url: 'api/billing/v1/finance',
});
export const getXpressDocument = (params: Record<string, unknown> = {}) =>
  getXpressDocumentHandler({ params });

const getTitleDescHandler = ServiceHandler({
  url: 'api/billing/v1/desc',
});
export const getTitleDesc = (params: Record<string, unknown> = {}) =>
  getTitleDescHandler({ params });

const getXpressStatusHandler = ServiceHandler({
  url: 'api/billing/v1/xpress-enabled',
});
export const getXpressStatus = (params: Record<string, unknown> = {}) =>
  getXpressStatusHandler({ params });

const getPaymentStatusHandler = ServiceHandler({
  url: 'api/billing/v1/oneInc/validate',
});
export const getPaymentStatus = (params: Record<string, unknown> = {}) =>
  getPaymentStatusHandler({ params });

const closePremFinanceHandler = ServiceHandler({
  url: 'api/billing/v1/pay/premiumFinance',
  method: 'put',
});
export const closePremFinance = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => closePremFinanceHandler({ data });

const createPremFinanceHandler = ServiceHandler({
  url: 'api/billing/v1/create/premiumFinance',
  method: 'post',
});
export const createPremFinance = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createPremFinanceHandler({ data });

const getBalanceOnPolicyHandler = ServiceHandler({
  url: 'api/billing/v1/balance/:policyId',
});
export const getBalanceOnPolicy = ({
  policyId,
}: {
  policyId: string | number;
}) => getBalanceOnPolicyHandler(policyId);

const getBalanceOnPolicyInvoiceHandler = ServiceHandler({
  url: 'api/billing/v1/invoice/balance/:policyId',
});
export const getBalanceOnPolicyInvoice = ({
  policyId,
}: {
  policyId: string | number;
}) => getBalanceOnPolicyInvoiceHandler(policyId);

const makePaymentFromCowbellHandler = ServiceHandler({
  url: '/api/billing/v1/external/applyPayment',
  method: 'post',
});
export const makePaymentFromCowbell = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => makePaymentFromCowbellHandler({ data });

const getInstallmentAmountHandler = ServiceHandler({
  url: '/api/billing/v1/get-installment',
  method: 'post',
});
export const getInstallmentAmount = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => getInstallmentAmountHandler({ data });

const updateTransactionHandler = ServiceHandler({
  url: '/api/billing/v1/transaction',
  method: 'put',
});
export const updateTransaction = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateTransactionHandler({ data });

const checkAgencyBilledInvoiceHandler = ServiceHandler({
  url: 'api/billing/v1/agencyBillInvoice/:month/:year',
});
export const checkAgencyBilledInvoice = ({
  month,
  year,
}: {
  month: string | number;
  year: string | number;
}) => checkAgencyBilledInvoiceHandler(month, year);

const createBillingContactHandler = ServiceHandler({
  url: 'api/billing/v1/contact',
  method: 'post',
});
export const createBillingContact = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createBillingContactHandler({ data });

const updateBillingContactHandler = ServiceHandler({
  url: ' /api/billing/v1/contact',
  method: 'put',
});
export const updateBillingContact = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateBillingContactHandler({ data });

const deleteBillingContactHandler = ServiceHandler({
  url: 'api/billing/v1/contact?id=:id',
  method: 'delete',
});
export const deleteBillingContact = ({ id }: { id: string | number }) =>
  deleteBillingContactHandler(id);

const fetchBillingContactQueryConfig = [
  { path: 'page' },
  { path: 'size' },
  { path: 'search' },
  {
    paramKey: 'sort',
    transform: ({ orderBy, order }: any) => {
      switch (orderBy) {
        case 'modified':
          if (orderBy && order) {
            return `${orderBy},${order}`;
          }
          break;
        default:
          return orderBy ? `${orderBy}.keyword,${order}` : null;
      }
    },
  },
  ...commonDateParamMappings,
];

const fetchBillingContactHandler = ServiceHandler({
  url: ' /api/billing/v1/contact',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(fetchBillingContactQueryConfig, params)
    ),
});
export const fetchBillingContact = (params: Record<string, unknown> = {}) =>
  fetchBillingContactHandler({ params });
